import { useState, useEffect } from 'react';
import { createModel } from 'hox';
import { purchasesService } from '../../purchasesService';
import {
	IPurchaseExamine,
	IPurchaseDetail,
	IPurchasesMaterial,
	IPurchaseTabEnum,
	IPurchaseDescType,
	IPurchasesDetailMethod,
} from '../../purchasesTypes';
import useGlobalModel from '@/model/globalModel';
import { formatInt, formatNum, getRandomStr } from '@/utils/utils';
import { IMaterialDetail } from '@/views/base/material/materialService';
import { message } from 'antd';
import { useLockFn } from 'ahooks';
import moment from 'moment';
import { convertRes2Blob } from '@/utils/exportFile';
import { salesService } from '@/views/sales/salesService';
import { ISettleParams } from '@/views/public/publicService';
import { ISaleSettlement } from '@/views/sales/salesTypes';

const initDesc = {
	numAll: 0,     //开单
	amountAll: 0,    //总计   （去除下浮，赠品的金额）
}
//初始收汇方式
const initMethod: IPurchasesDetailMethod = {
	key: "1",
	amount: "0",
	around: "",
	certificatesTime: "",
	collectionTime: "",
	paymentMethod: "",
	paymentTime: "",
	proportion: "",
}

export const usePurchasesDetailModel = createModel(function () {
	const { user: { shopCode, shopName } } = useGlobalModel(({ user }) => [user]);
	const initFormVal = {
		id: '',
		code: '',
		shopName,
	};
	const [tabActive, setTabActive] = useState<IPurchaseTabEnum>(IPurchaseTabEnum.MATERIAL_INFO); //tabs 选中
	//编辑缓存
	const [editFormVal, setEditFormVal] = useState<IPurchaseDetail>(initFormVal);
	const [editList, setEditList] = useState<IPurchasesMaterial[]>([]);
	const [editDesc, setEditDesc] = useState<IPurchaseDescType>(initDesc);
	const [editMethodList, setEditMethodList] = useState<IPurchasesDetailMethod[]>([initMethod]);

	//新增缓存
	const [addFormVal, setAddFormVal] = useState<IPurchaseDetail>(initFormVal);
	const [addList, setAddList] = useState<IPurchasesMaterial[]>([]);
	const [addDesc, setAddDesc] = useState<IPurchaseDescType>(initDesc);
	const [addMethodList, setAddMethodList] = useState<IPurchasesDetailMethod[]>([initMethod]);

	//添加商品弹框
	const [materialDialog, setMaterialDialog] = useState<boolean>(false);

	//添加收汇方式
	const addMethod = (mode: string) => {
		const setMethodList = mode === "edit" ? setEditMethodList : setAddMethodList;
		setMethodList(origin => [...origin, {
			...initMethod,
			amount: "0.00",
			proportion: "0.00%",
			key: getRandomStr()
		}])
	}


	useEffect(() => {
		let newDesc: IPurchaseDescType = { ...initDesc };
		editList.forEach(item => {
			newDesc.numAll += Number(item.num);
			newDesc.amountAll += Number(item.amount);
		});
		setEditDesc(newDesc);
		updateFormVal("edit", { amountTotal: formatNum(newDesc.amountAll) })
	}, [editList])

	useEffect(() => {
		let newDesc: IPurchaseDescType = { ...initDesc };
		addList.forEach(item => {
			newDesc.numAll += Number(item.num);
			newDesc.amountAll += Number(item.amount);
		});
		setAddDesc(newDesc);
		updateFormVal("add", { amountTotal: formatNum(newDesc.amountAll) })
	}, [addList])

	//重置页面缓存
	const resetPageCache = (mode: string) => {
		if (mode === 'edit') {
			setEditFormVal(initFormVal);
			setEditList([]);
			setEditMethodList([])
		} else if (mode === 'add') {
			setAddFormVal(initFormVal);
			setAddList([]);
			setAddMethodList([])
		}
		setTabActive(IPurchaseTabEnum.MATERIAL_INFO);
	};

	//主单修改
	const updateFormVal = (mode: string, val?: object | null) => {
		const setFormVal = mode === 'edit' ? setEditFormVal : setAddFormVal;
		setFormVal((origin: any) => (val ? { ...origin, ...val } : initFormVal));
	};

	//商品子表修改
	const updateList = (mode: string, val: IPurchasesMaterial[]) => {
		const setList = mode === 'edit' ? setEditList : setAddList;
		setList([...val]);
	};

	//商品子表添加
	const insertMaterial = (mode: string, arr: IMaterialDetail[]) => {
		const setList = mode === 'edit' ? setEditList : setAddList;
		setList((origin) => {
			let newArr: IPurchasesMaterial[] = [...origin];
			arr.forEach((material) => {
				//选中的是否存在列表
				let isExist = origin.find((item) => item.materialCode == material.code);
				if (!isExist) {
					let num = +formatInt(material.num);
					let price = +formatNum(material.buyingPrice);
					newArr.push({
						key: material.id,
						materialId: material.id, // "物料id",
						materialCode: material.code, // "物料编号",
						replaceCode: material.replaceCode, // "替换号", 
						materialName: material.name, // "物料名称",
						materialEname: material.nameEn, // "物料名称英文",
						typeCode: material.typeCode, // "物料类型",
						typeName: material.typeName, // "物料类型名称",
						model: material.model, // "规格",
						"unitCode": material.unitCode, // 
						"unitName": material.unitName, // "单位",
						"customsCode": material.customsCode, // "单位",
						warehouseCode: material.warehouseCode,// '仓库code';
						warehouseName: material.warehouseName, // '仓库名称';
						shelfCode: material.shelfCode,// '货位';
						num: num, // "询价数量",
						price: price, // "外卖单价",
						amount: price * num, // "外卖总价",
						note: '', // "备注" 
					});
				}
			});
			return newArr;
		});
		setMaterialDialog(false);
	};

	//商品子表删除
	const removeMaterial = (mode: string, selectedRowKeys: string[]) => {
		const setList = mode === 'edit' ? setEditList : setAddList;
		setList((origin) => origin.filter((item) => selectedRowKeys.indexOf(item?.key ?? '') < 0));
	};

	//获取一条数据
	const getPurchaseDetailOne = async (id: string) => {
		let { retData } = await purchasesService.one(id);
		let { details, detailsPayment, ...formVal } = retData;
		detailsPayment = detailsPayment ?? []
		if (retData) {
			setEditFormVal({
				...formVal,
				createTime:formVal.createTime ? moment(formVal.createTime) : null,
				updateTime:formVal.updateTime ? moment(formVal.updateTime) : null,
				purchaseCode: formVal.purchaseCode ?? formVal.code,
				estimatedTime: (formVal.estimatedTime ? moment(formVal.estimatedTime) : null),
				signingTime: formVal.signingTime ? moment(formVal.signingTime) : null,
				exWarehouseTime: formVal.exWarehouseTime ? moment(formVal.exWarehouseTime) : null,
				settlementTime:formVal.settlementTime ? moment(formVal.settlementTime) : null,
			});
			setEditList(details?.map((item) => ({ ...item, key: item.id ?? '' })) ?? []);
			setEditMethodList(detailsPayment.length > 0 ? detailsPayment?.map(item => ({
				...item,
				key: item.id ?? "",
				// paymentTime: item.paymentTime ? moment(item.paymentTime) : null,
				collectionTime: item.collectionTime ? moment(item.collectionTime) : null,
				// certificatesTime: item.certificatesTime ? moment(item.certificatesTime) : null,
			})) ?? [] : [initMethod]);
		}
		return retData;
	};


	//获取一条数据
	const getSalesDetail = async (code: string) => {
		let { retData } = await salesService.one(code);
		let { details, ...formVal } = retData;
		if (retData) {
			setAddFormVal(origin => ({ ...origin, saleCode: code }))
			if (details && details.length) {
				setAddList(details?.map(item => ({ ...item, key: item.id ?? "", saleDetailsId: item.id, id: undefined })) ?? []);
				message.success("已带出此外贸单的商品信息")
			} else {
				message.warning("此外贸单的单据的商品为空");
			}
		}
		return retData;
	};

	//新增 | 编辑
	const insertOrUpdatePurchase = useLockFn(async (mode: string, formVal: IPurchaseDetail, refresh = true) => {
		const materialList = mode === 'edit' ? editList : addList;
		const detailsPayment = mode === "edit" ? editMethodList : addMethodList;
		let data = {
			...formVal,
			signingTime:
				formVal.signingTime instanceof Object
					? formVal.signingTime.format('YYYY-MM-DDTHH:mm:ss')
					: null,
			exWarehouseTime:
				formVal.exWarehouseTime instanceof Object
					? formVal.exWarehouseTime.format('YYYY-MM-DDTHH:mm:ss')
					: null,
			estimatedTime:
				formVal.estimatedTime instanceof Object
					? formVal.estimatedTime.format('YYYY-MM-DDTHH:mm:ss')
					: null,
			settlementTime:
					formVal.settlementTime instanceof Object
					? formVal.settlementTime.format('YYYY-MM-DDTHH:mm:ss')
					: null,
			shopCode,
			createTime:
			formVal.createTime instanceof Object 
			?
			 formVal.createTime.format('YYYY-MM-DDTHH:mm:ss') :null,
			 updateTime:formVal.updateTime instanceof Object
			 ? formVal.updateTime.format('YYYY-MM-DDTHH:mm:ss'):null,
			details: materialList,
			//收汇方式 处理日期
			detailsPayment: detailsPayment.map(item => ({
				...item,
				// paymentTime: item.paymentTime instanceof Object ? item.paymentTime.format('YYYY-MM-DDTHH:mm:ss') : null,
				collectionTime: item.collectionTime instanceof Object ? item.collectionTime.format('YYYY-MM-DDTHH:mm:ss') : null,
				// certificatesTime: item.certificatesTime instanceof Object ? item.certificatesTime.format('YYYY-MM-DDTHH:mm:ss') : null,
			}))
		};
		//新增
		if (mode === 'edit') {
			let { retData } = await purchasesService.update({
				...data,
				id: editFormVal.id,
				code: editFormVal.code ?? '',
				
			});
			refresh && getPurchaseDetailOne(editFormVal.code)
			refresh && message.success('编辑成功');
			return retData;
		} else {
			let { retData } = await purchasesService.insert(data);
			message.success('新增成功,自动转换至编辑详情页');
			return retData;
		}
	});

	//提交审核
	const submitPurchase = useLockFn(async (id?: string) => {
		let formRes = await insertOrUpdatePurchase("edit", editFormVal, false);
		let { retData } = await purchasesService.submitPurchase(editFormVal.code);
		getPurchaseDetailOne(editFormVal.code)
		message.success('提交审核成功');
		return retData;
	});
	//总监审核
	const examineManager = useLockFn(async (obj: IPurchaseExamine) => {
		let { retData } = await purchasesService.examineManager(obj);
		getPurchaseDetailOne(editFormVal.code)
		message.success('已提交总监审核');
		return retData;
	});
	//财务审核
	const examineReckoner = useLockFn(async (obj: IPurchaseExamine) => {
		let { retData } = await purchasesService.examineReckoner(obj);
		getPurchaseDetailOne(editFormVal.code)
		message.success('已提交财务审核');
		return retData;
	});

	//入库
	const whConfirm = useLockFn(async () => {
		let { retData } = await purchasesService.whConfirm({
			shopCode,
			code: editFormVal.code
		});
		getPurchaseDetailOne(editFormVal.code)
		message.success("入库成功");
		return retData;
	});

	//采购合同明细导出
	const exportDetails = useLockFn(async () => {
		let result = await purchasesService.exportDetails({
			shopCode,
			code: editFormVal.code
		});
		let fileName: string = `${editFormVal.code}-${moment().format("YYYY-MM-DD")}.pdf`;
		convertRes2Blob(result, fileName);
	})
	/**
		 * 结算
		 */
	const [settleVisible, setSettleVisible] = useState<boolean>(false);   //结算弹框

	const purchaseSettle = useLockFn(async (val: ISettleParams) => {
		let data: ISettleParams = {
			...val,
			shopCode,
		};
		let { retData, retMsg } = await purchasesService.settle(data);
		if (retData) {
			message.success("结算成功")
			setSettleVisible(false);
			getPurchaseDetailOne(editFormVal.code)
		} else {
			message.warning(retMsg)
		}
	});

	//结算
	const receiptSettle = useLockFn(async (data: ISaleSettlement[]) => {
		let { retData } = await purchasesService.receiptSettle(data);
		if (retData) {
			message.success("操作成功")
			setSettleVisible(false);
			// getPurchaseDetailOne(editFormVal.code)
		}
		return retData;
	});

	//结算已完结
	const settleStatu = useLockFn(async () => {
		let { retData } = await purchasesService.settleStatu(editFormVal.code);
		message.success("结算已完结")
		setSettleVisible(false);
		getPurchaseDetailOne(editFormVal.code)
	});

	return {
		/***field***/
		editFormVal,
		editList,
		addFormVal,
		addList,
		tabActive, addDesc, editDesc,
		setTabActive,
		materialDialog,
		setMaterialDialog,
		/***method***/
		examineManager,
		examineReckoner,
		updateFormVal,
		updateList,
		removeMaterial,
		insertMaterial,
		resetPageCache,
		getPurchaseDetailOne,
		submitPurchase,
		insertOrUpdatePurchase,
		whConfirm, exportDetails, getSalesDetail,
		settleVisible, setSettleVisible,
		purchaseSettle, receiptSettle, settleStatu,
		setEditMethodList, addMethodList, setAddMethodList, addMethod, editMethodList
	};
});
